/**
 * @copyright
 * Copyright (c) 2024 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { createAsyncThunk } from "@reduxjs/toolkit";
import { isAxiosError } from "axios";

import { ApiErrorCode } from "~/constants";
import AuthApi from "~/features/auth/AuthApi";
import { PostLoginPayload, PostSend2FAEmailPayload, PostValidate2FATokenPayload } from "~/features/auth/types";
import createThunkError from "~/utils/createThunkError";
import getApiErrorCodeFromResponse from "~/utils/errors/getApiErrorCodeFromResponse";

const authInfoPayloadCreator = async () => {
  const accountInfoPromise = await AuthApi.getInfo();

  return {
    account: accountInfoPromise.data.result,
    viewOnly: accountInfoPromise.data.viewOnly,
    twoFactorAuthRequired: accountInfoPromise.data.twoFactorAuthRequired,
  };
};

export const initAuthAsync = createAsyncThunk("auth/initAuth", authInfoPayloadCreator);

export const loginAsync = createAsyncThunk("auth/login", async (data: PostLoginPayload, thunkAPI) => {
  try {
    await AuthApi.postLogin(data);

    await thunkAPI.dispatch(getAccountInfo());
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 401) {
      return thunkAPI.rejectWithValue(createThunkError({ message: "error.invalid_login_info" }));
    } else if (isAxiosError(error) && error.response?.status === 400 && error.response?.data) {
      return thunkAPI.rejectWithValue(
        createThunkError({
          message: "Weak password",
          apiErrorCode: ApiErrorCode.BAD_REQUEST,

          data: error.response.data,
        }),
      );
    }

    throw error;
  }
});

export const getAccountInfo = createAsyncThunk("auth/accountInfo", authInfoPayloadCreator);

export const logoutAsync = createAsyncThunk("auth/logout", async () => {
  await AuthApi.postLogout();
});

export const validate2FAToken = createAsyncThunk(
  "auth/validate2FAToken",
  async (data: PostValidate2FATokenPayload, thunkAPI) => {
    const response = await AuthApi.postValidate2FAToken(data);

    if (response.data.result === "Different session.") {
      return thunkAPI.rejectWithValue("Different session.");
    }

    if (response.status !== 200) {
      const apiErrorCode = getApiErrorCodeFromResponse(response);
      return thunkAPI.rejectWithValue(apiErrorCode);
    }
  },
);

export const send2FAEmail = createAsyncThunk("auth/send2FAEmail", async (data: PostSend2FAEmailPayload, thunkAPI) => {
  const response = await AuthApi.postSend2FAEmail(data);

  if (response.status !== 200) {
    const apiErrorCode = getApiErrorCodeFromResponse(response);
    return thunkAPI.rejectWithValue(apiErrorCode);
  }

  return response.data.result.email;
});
export const check2FASession = createAsyncThunk("auth/check2FASession", async () => {
  await AuthApi.getCheck2FASession();
});
